import { useState, useEffect } from 'react';
import WizardUpdateConfirmModal from './WizardUpdateConfirmModal';
import SplitUpdateConfirmModal from './SplitUpdateConfirmModal';
import BudgetSyncModal from './BudgetSyncModal';
import DebouncedNumberInput from './DebouncedNumberInput';
import ConfirmModal from './ConfirmModal';
import TransferFields from './IntentFields/TransferFields';
import DefaultFields from './IntentFields/DefaultFields';
import TaxFields from './IntentFields/TaxFields';
import { APIClient } from "../helpers/api_helper";
import { CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setFilterData } from '../slices/filters/thunk';
import dayjs from 'dayjs';
import usecustomStyles from "./customStyles";
import IntentTypeSelect from "./IntentTypeSelect";
import TaxRateSelect from "./TaxRateSelect";
import { Alert, Button, Checkbox, Col, Divider, Drawer, Form,
  Input, Row, Select, Segmented, Space, Switch, Tooltip,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import MoneyDisplay from '../Common/MoneyDisplay';
import HistoryTimeline from './HistoryTimeline';
import IntentComments from './IntentComments';

const { Option } = Select;
const { TextArea } = Input;
const customStyles = usecustomStyles();
const api = new APIClient();

// Boolean columns without null=True. In mass edit their BoolToggle is tri-state, where
// null stands for "leave unchanged" and must never be sent to bulk_update.
const NON_NULLABLE_BOOL_FIELDS = ['is_paid', 'is_issued'];

const OverrideLabel = ({ label, field, overriddenFields }) => (
  <span>
    {label}
    {overriddenFields?.includes(field) && (
      <Tooltip title="Vrednost je bila ročno spremenjena.">
        <EditOutlined style={{ color: '#1677ff', marginLeft: 4 }} />
      </Tooltip>
    )}
  </span>
);

const IntentDrawer = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const repeatPeriod = useWatch('repeat_period', form);
  const amount = useWatch('amount', form);

  const [activeDrawerTab, setActiveDrawerTab] = useState('data');
  const [showWizardUpdateConfirmModal, setshowWizardUpdateConfirmModal] = useState(false);
  const [showBudgetSyncModal, setShowBudgetSyncModal] = useState(false);
  const [showDeletionConfirmModal, setShowDeletionConfirmModal] = useState(false);
  const [showMassEditConfirmModal, setShowMassEditConfirmModal] = useState(false);
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [showSplitConfirmModal, setShowSplitConfirmModal] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [splitEditMode, setSplitEditMode] = useState('master');
  const [wizardEditMode, setWizardEditMode] = useState('all');
  const [includePastUnpaid, setIncludePastUnpaid] = useState(false);
  const [includePastPaid, setIncludePastPaid] = useState(false);
  const [activeEditDimension, setActiveEditDimension] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [pendingMassEditValues, setPendingMassEditValues] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const [intentData, setIntentData] = useState(null);
  const [saving, setSaving] = useState(false);
  const typeValue = useWatch('type', form);
  // Bumped whenever the drawer (re)initialises, to remount TaxRateSelect so its
  // "custom rate box is open" latch doesn't survive into the next intent.
  const [formEpoch, setFormEpoch] = useState(0);

  const isMassEdit = props.massEdit === true;
  const isEdit = props.editedIntentId !== null && !isMassEdit;

  const applyMode = (mode, resp) => {
    if (!resp?.split_master) return;
    if (mode === 'master') {
      form.setFieldsValue({
        amount: resp.split_master.amount,
        amount_no_tax: resp.split_master.amount_no_tax,
        tax_rate: resp.split_master.tax_rate,
        tax_amount: resp.split_master.tax_amount,
      });
    } else {
      form.setFieldsValue({
        amount: resp.amount,
        amount_no_tax: resp.amount_no_tax,
        tax_rate: resp.tax_rate,
        tax_amount: resp.tax_amount,
      });
    }
  };
  useEffect(() => {
    if (isMassEdit && props.open) {
      form.resetFields();
      setIntentData(null);
      setActiveDrawerTab('data');
    }
  }, [props.open, isMassEdit]);

  useEffect(() => {
    if (!props.open) return;
    if (isMassEdit) return;
    if (props.editedIntentId) return;
    if (props.defaultValues) {
      form.setFieldsValue(props.defaultValues);
    }
  }, [props.open]);

  useEffect(() => {
    setActiveDrawerTab('data');
    setValidationErrors([]);
    setFormEpoch(e => e + 1);
    setWizardEditMode('all');
    setIncludePastUnpaid(false);
    setIncludePastPaid(false);
    if (props.editedIntentId) {
      try {
        api.get(`/api/intents/${props.editedIntentId}/`, {})
          .then(resp => {
            const respData = {
              ...resp,
              date_invoice_recieved: resp.date_invoice_recieved ? dayjs(resp.date_invoice_recieved) : null,
              date_service_performed: resp.date_service_performed ? dayjs(resp.date_service_performed) : null,
              date_to_be_paid: resp.date_to_be_paid ? dayjs(resp.date_to_be_paid) : null,
              repeat_to_date: resp.repeat_to_date ? dayjs(resp.repeat_to_date) : null,
            };
            setIntentData(respData);
            form.setFieldsValue(respData);
            if (Array.isArray(resp.split_siblings) && resp.split_siblings.length > 0) {
              form.setFieldsValue({
                project: null,
                project_splits: resp.split_siblings.map(s => ({
                  project: s.project,
                  split_percent: s.split_percent,
                })),
              });
              setSplitEditMode('master');
              applyMode('master', resp);
              setActiveEditDimension('split');
            } else if (resp.wizard) {
              setActiveEditDimension('wizard');
            } else {
              setActiveEditDimension(null);
            }
          });
      } catch (error) {
        console.error(error);
      }
    } else {
      form.resetFields();
      setIntentData(null);
      setActiveEditDimension(null);
    }
  }, [props.isOpen, props.editedIntentId, refreshTick]);

  const handleMassEditSave = (setValues) => {
    const payload = {
      ids: props.massEditIds,
      ...setValues,
      ...(setValues.date_invoice_recieved && { date_invoice_recieved: setValues.date_invoice_recieved.format('YYYY-MM-DD') }),
      ...(setValues.date_service_performed && { date_service_performed: setValues.date_service_performed.format('YYYY-MM-DD') }),
      ...(setValues.date_to_be_paid && { date_to_be_paid: setValues.date_to_be_paid.format('YYYY-MM-DD') }),
      ...(setValues.repeat_to_date && { repeat_to_date: setValues.repeat_to_date.format('YYYY-MM-DD') }),
    };
    setSaving(true);
    api.update('/api/intents/bulk_update/', payload, { successMsg: `Posodobljeno ${props.massEditIds?.length} transakcij` })
      .then(() => {
        props.onDone();
        form.resetFields();
        setPendingMassEditValues(null);
        setShowMassEditConfirmModal(false);
      })
      .catch(err => console.error('Mass edit error:', err))
      .finally(() => setSaving(false));
  };

  const handleConfirmedSave = (values, wizardChoice, budgetSyncMode, splitChoice) => {
    // Convert undefined → null so JSON.stringify doesn't silently drop cleared fields (e.g. tag, company)
    const sanitized = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v === undefined ? null : v]));
    const payload = {
        ...sanitized,
        wizard_choice: wizardChoice,
        ...(budgetSyncMode && { budget_sync_mode: budgetSyncMode }),
        ...(splitChoice && { split_choice: splitChoice }),
        date_invoice_recieved: sanitized.date_invoice_recieved ? sanitized.date_invoice_recieved.format('YYYY-MM-DD') : null,
        date_service_performed: sanitized.date_service_performed ? sanitized.date_service_performed.format('YYYY-MM-DD') : null,
        date_to_be_paid: sanitized.date_to_be_paid ? sanitized.date_to_be_paid.format('YYYY-MM-DD') : null,
        repeat_to_date: sanitized.repeat_to_date ? sanitized.repeat_to_date.format('YYYY-MM-DD') : null,
    };

    const hasSplits = Array.isArray(payload.project_splits) && payload.project_splits.length > 0;
    if (hasSplits) {
      payload.project = null;
    } else {
      delete payload.project_splits;
    }
    delete payload.split_siblings;
    delete payload.split_master;

    // Editing an existing split child: structural changes go through /resplit/.
    // The regular PATCH must not carry project_splits so it doesn't trip serializer validation.
    if (isEdit && intentData?.split_siblings) {
      delete payload.project_splits;
      delete payload.project;
    }

    // Active-dimension dispatch:
    //   split  + master → POST /update_master/  (this occurrence only)
    //   split  + child  → PATCH + split_choice=single
    //   wizard + all    → PATCH + wizard_choice=all + past flags
    //   wizard + single → PATCH + wizard_choice=single
    const dim = activeEditDimension;
    if (dim === 'split' && isEdit && intentData?.split_siblings) {
      setSaving(true);
      const apiCall = splitEditMode === 'master'
        ? api.post(`/api/intents/${props.editedIntentId}/update_master/`, payload, { successMsg: 'SKUPNA transakcija posodobljena' })
        : api.update(`/api/intents/${props.editedIntentId}/`, { ...payload, split_choice: 'single' });
      apiCall
        .then(() => {
          props.onDone();
          setRefreshTick(x => x + 1);
          setPendingValues(null);
          setShowSplitConfirmModal(false);
        })
        .catch(err => console.error('API error:', err))
        .finally(() => setSaving(false));
      return;
    }

    if (dim === 'wizard' && isEdit && intentData?.wizard) {
      payload.wizard_choice = wizardEditMode;
      if (wizardEditMode === 'all') {
        payload.wizard_include_past_unpaid = includePastUnpaid;
        payload.wizard_include_past_paid = includePastPaid;
      }
      // On a split intent, the wizard dimension edits the MASTER amount.
      // Route through update_master so `amount` is treated as the master
      // total (not derived via percent). The wizard params drive
      // cross-occurrence propagation inside update_master.
      if (intentData?.split_siblings) {
        setSaving(true);
        api.post(
          `/api/intents/${props.editedIntentId}/update_master/`,
          payload,
          { successMsg: wizardEditMode === 'all'
              ? 'Posodobljene vse ponovitve v dosegu'
              : 'Posodobljena samo ta ponovitev' }
        )
          .then(() => {
            props.onDone();
            setRefreshTick(x => x + 1);
            setPendingValues(null);
            setshowWizardUpdateConfirmModal(false);
          })
          .catch(err => console.error('API error:', err))
          .finally(() => setSaving(false));
        return;
      }
    }

    const apiCall = isEdit
        ? api.update(`/api/intents/${props.editedIntentId}/`, payload)
        : api.create('/api/intents/', payload);

    setSaving(true);
    apiCall
      .then(resp => {
          props.onDone();
          if (budgetSyncMode === 'new_version') props.onVersionCreated?.();
          form.resetFields();
          setPendingValues(null);
          setshowWizardUpdateConfirmModal(false);
        })
      .catch(err => console.error('API error:', err))
      .finally(() => setSaving(false));
    };

  const handleConfirmedDelete = () => {
      // Only the WIZARD dimension scopes deletion across occurrences. Split
      // deletion always cascades to the whole split group of this occurrence,
      // regardless of split mode.
      const isWizard = intentData?.wizard != null;
      const params = new URLSearchParams();
      if (isWizard && activeEditDimension === 'wizard') {
        params.set('wizard_choice', wizardEditMode);
        if (wizardEditMode === 'all') {
          if (includePastUnpaid) params.set('wizard_include_past_unpaid', 'true');
          if (includePastPaid) params.set('wizard_include_past_paid', 'true');
        }
      }
      const qs = params.toString();
      const url = `/api/intents/${props.editedIntentId}/${qs ? `?${qs}` : ''}`;
      api.delete(url, {})
        .then(resp => {
          props.onDone()
          form.resetFields();
          setPendingValues(null);
          setShowDeletionConfirmModal(false);
        })
        .catch(error => {
          console.error(error)
        })
  }

  const handleCancel = () => {
    form.resetFields();
    props.onCancel()
  }

  const onSubmit = () => {
    setValidationErrors([]);
    if (isMassEdit) {
      const allValues = form.getFieldsValue();
      // Include ALL touched fields, even null/undefined — null means "clear this field" (e.g. removing a tag).
      // undefined is converted to null so JSON serialization doesn't silently drop the key.
      const setValues = Object.fromEntries(
        Object.entries(allValues)
          .filter(([key]) => form.isFieldTouched(key))
          // Booleans are NOT NULL in the DB; null here means "brez sprememb", so drop them.
          .filter(([key, v]) => !(NON_NULLABLE_BOOL_FIELDS.includes(key) && v == null))
          .map(([key, v]) => [key, v === undefined ? null : v])
      );
      setPendingMassEditValues(setValues);
      setShowMassEditConfirmModal(true);
      return;
    }
    form
      .validateFields()
      .then((values) => {
        // Active-dimension dispatch. The split confirm modal handles the
        // split dimension; the wizard confirm modal handles the wizard one.
        if (activeEditDimension === 'split' && intentData?.split_siblings) {
          setPendingValues(values);
          setShowSplitConfirmModal(true);
          return;
        }
        if (activeEditDimension === 'wizard' && intentData?.wizard) {
          setPendingValues(values);
          setshowWizardUpdateConfirmModal(true);
          return;
        }
        if (intentData?.proj_budget_expense_plan_row) {
          const checkPayload = {
            ...values,
            date_invoice_recieved: values.date_invoice_recieved ? values.date_invoice_recieved.format('YYYY-MM-DD') : null,
            date_service_performed: values.date_service_performed ? values.date_service_performed.format('YYYY-MM-DD') : null,
            date_to_be_paid: values.date_to_be_paid ? values.date_to_be_paid.format('YYYY-MM-DD') : null,
          };
          api.post(`/api/intents/${props.editedIntentId}/check_budget_sync/`, checkPayload, { successMsg: null })
            .then(resp => {
              if (resp.needs_dialog) {
                setPendingValues(values);
                setShowBudgetSyncModal(true);
              } else {
                handleConfirmedSave(values);
              }
            });
        } else if (intentData?.wizard != null) {
          // Wizard intent (non-split). The mode + past flags are already set
          // via the inline Alert; the confirm modal is info-only.
          setPendingValues(values);
          setshowWizardUpdateConfirmModal(true);
        } else {
          handleConfirmedSave(values);
        }
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
        setValidationErrors(info?.errorFields || []);
      });
  };

  const isIntentEditable = isMassEdit || ['INCOME', 'EXPENSE', 'TRANSFER'].includes(typeValue);
  const isCreation = props.editedIntentId == null && !isMassEdit;
  const isReadOnly = isMassEdit ? false : (intentData?.id && !isIntentEditable);
  const taxRateReadOnly =
    ['TAX', 'TRANSFER'].includes(typeValue)
    || (intentData?.split_siblings && splitEditMode === 'child');

  useEffect(() => {
    if (typeValue === 'TRANSFER') {
      form.setFieldsValue({ tax_rate: 0 });
    }
  }, [typeValue]);

  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

  // The four amount fields recalculate away from whichever one was just edited:
  // that field is never written back, the other three are derived from it. Kept
  // synchronous and client-side (the old /calc_amounts/ round-trip landed ~1s
  // later, rewriting fields under the caret and re-triggering itself).
  //
  // Every branch leaves amount_no_tax + tax_amount === amount exactly, so the
  // server can store the triple verbatim instead of re-deriving it from the 2dp
  // tax_rate — see apply_amounts() in backend/intents/procedures.py.
  const recalcFrom = (field) => {
    const values = form.getFieldsValue();
    const net = num(values.amount_no_tax);
    const gross = num(values.amount);
    const vat = num(values.tax_amount);
    const rate = num(values.tax_rate);

    // TAX / TRANSFER / split children have a fixed rate and no DDV field, so the
    // rate stays the anchor there and is never re-derived from the two amounts.
    if (taxRateReadOnly) {
      if (rate == null) return;
      if (field === 'amount') {
        if (gross == null) return;
        const n = round2(gross / (1 + rate / 100));
        form.setFieldsValue({ amount_no_tax: n, tax_amount: round2(gross - n) });
      } else if (net != null) {
        const g = round2(net * (1 + rate / 100));
        form.setFieldsValue({ amount: g, tax_amount: round2(g - net) });
      }
      return;
    }

    if (field === 'amount_no_tax' || field === 'tax_rate') {
      // Net + rate anchor: derive gross, then DDV as the remainder.
      if (net == null || rate == null) return;
      const g = round2(net * (1 + rate / 100));
      form.setFieldsValue({ amount: g, tax_amount: round2(g - net) });
      return;
    }

    if (field === 'tax_amount') {
      // The typed DDV wins: gross is the sum, the rate is the derived label.
      if (vat == null) return;
      const anchor = net != null ? net : (gross != null ? round2(gross - vat) : null);
      if (anchor == null || anchor === 0) return;
      form.setFieldsValue({
        amount_no_tax: anchor,
        amount: round2(anchor + vat),
        tax_rate: round2((vat / anchor) * 100),
      });
      return;
    }

    if (field === 'amount') {
      // The typed gross wins. Anchor on net when there is one; with no net yet
      // (gross entered first) fall back to splitting it by the current rate.
      if (gross == null) return;
      if (net != null && net !== 0) {
        const t = round2(gross - net);
        form.setFieldsValue({ tax_amount: t, tax_rate: round2((t / net) * 100) });
      } else if (rate != null) {
        const n = round2(gross / (1 + rate / 100));
        form.setFieldsValue({ amount_no_tax: n, tax_amount: round2(gross - n) });
      }
    }
  };

  const taxIntentInfoComponent = intentData?.tax_intent_title ? <span>{intentData?.tax_intent_title}</span> : null;

  const taxAmountOverridden = typeValue === 'TAX' && intentData?.manually_overridden_fields?.includes('amount');

  // Live divergence: the TAX amount currently in the form differs from the
  // system-computed value — even before saving. Lets us surface the info label
  // + "Ponastavi" the moment the user edits the amount, not only after save.
  const taxAmountDiverges =
    typeValue === 'TAX' &&
    intentData?.tax_system_amount != null &&
    amount != null &&
    Number(amount) !== Number(intentData.tax_system_amount);

  const showTaxOverrideInfo = taxAmountOverridden || taxAmountDiverges;

  const handleResetAmountOverride = () => {
    api
      .post(`/api/intents/${props.editedIntentId}/reset_amount_override/`, {}, { successMsg: 'Znesek ponastavljen na sistemsko vrednost' })
      .then(() => {
        setRefreshTick(x => x + 1);
        props.onIntentChanged?.();
      });
  };

  // Open the intents list filtered to this TAX intent's related income/expense
  // transactions (those summed into this taxation period).
  const handleShowRelatedIntents = () => {
    const id = props.editedIntentId;
    if (!id) return;
    const filter = { tax_intent: id, page: 1 };
    dispatch(setFilterData(filter));
    navigate(`/intents#${new URLSearchParams(filter).toString()}`);
    props.onCancel?.();
  };

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={(changed) => {
          // Manually picking a tax rate anchors on net (amount_no_tax) and
          // recomputes gross + DDV. onValuesChange only fires for user input,
          // not for our own setFieldsValue (e.g. the DDV→rate derivation), so
          // it can't fight the other recalculation paths.
          if ('tax_rate' in changed) {
            recalcFrom('tax_rate');
          }
        }}
      >
        <Drawer
          title={
            <Space>
              <span>
                {isMassEdit
                  ? `Množično urejanje (${props.massEditIds?.length} transakcij)`
                  : isCreation
                  ? "Ustvari novo transakcijo"
                  : `Transakcija #${props.editedIntentId}`}
              </span>
              {!isCreation && !isMassEdit && (
                <Segmented
                  size="small"
                  value={activeDrawerTab}
                  onChange={setActiveDrawerTab}
                  options={[
                    { label: 'Podatki', value: 'data' },
                    {
                      label: `Komentarji${intentData?.comments_count ? ` (${intentData.comments_count})` : ''}`,
                      value: 'comments',
                    },
                    { label: 'Zgodovina', value: 'history' },
                  ]}
                />
              )}
            </Space>
          }
          width={750}
          onClose={handleCancel}
          open={props.open}
          extra={
            activeDrawerTab === 'data' && (
              <Space>
                {!isMassEdit && <Button disabled={!isIntentEditable || isCreation} onClick={() => setShowDeletionConfirmModal(true)} danger><DeleteOutlined /></Button>}
                <Button onClick={handleCancel}><CloseOutlined /></Button>
                <Button disabled={saving} loading={saving} onClick={onSubmit} type="primary">Shrani</Button>
              </Space>
            )
          }
        >
          {activeDrawerTab === 'data' && (
                <>
                  {validationErrors.length > 0 && (
                    <Alert
                      type="error"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Obrazec ima napake"
                      description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {validationErrors.map((f, i) => (
                            <li key={i}>
                              {f.errors?.[0] ||
                                (Array.isArray(f.name) ? f.name.join('.') : f.name)}
                            </li>
                          ))}
                        </ul>
                      }
                    />
                  )}
                  {isEdit && ['INCOME', 'EXPENSE'].includes(typeValue) && intentData?.tax_period_locked && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Davčna obveznost za to obdobje je ročno nastavljena (zaklenjena)."
                      description={
                        <>
                          Sprememba te transakcije bo posodobila sistemsko izračunan znesek, ne pa ročno nastavljenega (zaklenjenega) davčnega zneska. Po potrebi ponastavi davčni znesek na davčni obveznosti.
                          {props.onOpenIntent && intentData?.tax_intent && (
                            <div style={{ marginTop: 4 }}>
                              <Button
                                type="link"
                                size="small"
                                style={{ paddingLeft: 0 }}
                                onClick={() => props.onOpenIntent(intentData.tax_intent)}
                              >
                                Odpri davčno obveznost{intentData?.tax_intent_title ? ` (${intentData.tax_intent_title})` : ''} →
                              </Button>
                            </div>
                          )}
                        </>
                      }
                    />
                  )}
                  {intentData?.split_siblings && (() => {
                    const isActive = activeEditDimension === 'split';
                    return (
                      <Alert
                        type="info"
                        showIcon
                        style={{
                          marginBottom: 12,
                          opacity: isActive ? 1 : 0.55,
                          borderLeft: isActive ? '4px solid #1677ff' : '4px solid transparent',
                          cursor: isActive ? 'default' : 'pointer',
                        }}
                        onClick={isActive ? undefined : () => setActiveEditDimension('split')}
                        message={
                          `Ta transakcija je razdeljena med ${intentData.split_siblings.length} ` +
                          (intentData.split_siblings.length === 2 ? 'projekta' : 'projekte')
                        }
                        description={
                          <>
                            <Space>
                              <span
                                style={{
                                  fontWeight: splitEditMode === 'master' ? 600 : 400,
                                  opacity: splitEditMode === 'master' ? 1 : 0.55,
                                }}
                              >
                                {`Urejanje VSEH projektnih transakcij (${intentData.split_siblings.length})`}
                              </span>
                              <Switch
                                checked={splitEditMode === 'child'}
                                disabled={!isActive}
                                onChange={(checked) => {
                                  const v = checked ? 'child' : 'master';
                                  setSplitEditMode(v);
                                  applyMode(v, intentData);
                                }}
                              />
                              <span
                                style={{
                                  fontWeight: splitEditMode === 'child' ? 600 : 400,
                                  opacity: splitEditMode === 'child' ? 1 : 0.55,
                                }}
                              >
                                Urejanje TRENUTNE PROJEKTNE transakcije (omejeno)
                              </span>
                            </Space>
                            {!isActive && (
                              <div style={{ fontStyle: 'italic', marginTop: 4 }}>
                                Klikni za urejanje razdelitve
                              </div>
                            )}
                          </>
                        }
                      />
                    );
                  })()}
                  {intentData?.wizard && (() => {
                    const isActive = activeEditDimension === 'wizard';
                    const activateWizard = () => {
                      setActiveEditDimension('wizard');
                      // On a split intent, wizard-dimension edits the master
                      // amount across occurrences — show the master total.
                      if (intentData?.split_siblings) {
                        setSplitEditMode('master');
                        applyMode('master', intentData);
                      }
                    };
                    return (
                      <Alert
                        type="info"
                        showIcon
                        style={{
                          marginBottom: 12,
                          opacity: isActive ? 1 : 0.55,
                          borderLeft: isActive ? '4px solid #1677ff' : '4px solid transparent',
                          cursor: isActive ? 'default' : 'pointer',
                        }}
                        onClick={isActive ? undefined : activateWizard}
                        message="Ta transakcija se ponavlja"
                        description={
                          <>
                            <Space>
                              <span
                                style={{
                                  fontWeight: wizardEditMode === 'all' ? 600 : 400,
                                  opacity: wizardEditMode === 'all' ? 1 : 0.55,
                                }}
                              >
                                {`Urejanje VSEH ponovljajočih transakcij${
                                  intentData?.wizard_occurrence_count
                                    ? ` (${intentData.wizard_occurrence_count})`
                                    : ''
                                }`}
                              </span>
                              <Switch
                                checked={wizardEditMode === 'single'}
                                disabled={!isActive}
                                onChange={(checked) =>
                                  setWizardEditMode(checked ? 'single' : 'all')
                                }
                              />
                              <span
                                style={{
                                  fontWeight: wizardEditMode === 'single' ? 600 : 400,
                                  opacity: wizardEditMode === 'single' ? 1 : 0.55,
                                }}
                              >
                                Urejanje samo TRENUTNE transakcije
                              </span>
                            </Space>
                            {wizardEditMode === 'all' && (
                              <div style={{ marginTop: 8 }}>
                                <Checkbox
                                  checked={includePastUnpaid}
                                  disabled={!isActive}
                                  onChange={(e) => setIncludePastUnpaid(e.target.checked)}
                                >
                                  Vključi pretekle neplačane ponovitve
                                </Checkbox>
                                <br />
                                <Checkbox
                                  checked={includePastPaid}
                                  disabled={!isActive}
                                  onChange={(e) => setIncludePastPaid(e.target.checked)}
                                >
                                  Vključi že plačane ponovitve
                                </Checkbox>
                              </div>
                            )}
                            {!isActive && (
                              <div style={{ fontStyle: 'italic', marginTop: 4 }}>
                                Klikni za urejanje ponovitev
                              </div>
                            )}
                          </>
                        }
                      />
                    );
                  })()}
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="type"
                        label="Tip"
                        rules={isMassEdit ? [] : [{ required: true, message: 'Prosim izberite tip' }]}
                      >
                        <IntentTypeSelect disabled={isEdit} hidden={isCreation ? ['TAX'] : []} />
                      </Form.Item>
                    </Col>
                  </Row>

                  {(typeValue != null || isMassEdit) && (
                    <>
                    <Row justify="space-between">
                      <Col span={11}>
                        <Form.Item
                          name="amount_no_tax"
                          label={<OverrideLabel label="Znesek brez DDV" field="amount_no_tax" overriddenFields={intentData?.manually_overridden_fields} />}
                          rules={isMassEdit ? [] : [{ required: true, message: 'Prosim vnesite znesek' }]}
                          >
                          <DebouncedNumberInput
                            value={form.getFieldValue('amount_no_tax')}
                            onChange={(val) => {
                              form.setFieldsValue({ amount_no_tax: val });
                              recalcFrom('amount_no_tax');
                            }}
                            disabled={isReadOnly || (intentData?.split_siblings && splitEditMode === 'child')}
                            placeholder="Vnesite znesek"
                            min={typeValue === 'TAX' ? -Infinity : 0}
                            />
                        </Form.Item>
                      </Col>

                      <Col span={11}>
                        <Form.Item
                          name="amount"
                          label={<OverrideLabel label="Znesek z DDV" field="amount" overriddenFields={intentData?.manually_overridden_fields} />}
                          rules={isMassEdit ? [] : [{ required: true, message: 'Prosim vnesite znesek' }]}
                          extra={typeValue === 'TAX' ? 'Uredi za spreminjanje vrednosti DDV.' : undefined}
                          >
                          <DebouncedNumberInput
                            value={form.getFieldValue('amount')}
                            onChange={(val) => {
                              form.setFieldsValue({ amount: val });
                              recalcFrom('amount');
                            }}
                            disabled={(isReadOnly && typeValue !== 'TAX') || (intentData?.split_siblings && splitEditMode === 'child')}
                            placeholder="Vnesite znesek"
                            min={typeValue === 'TAX' ? -Infinity : 0}
                            />
                        </Form.Item>
                        {showTaxOverrideInfo && (
                          <div style={{ marginTop: -8, marginBottom: 8, fontSize: 12, color: '#888' }}>
                            <EditOutlined style={{ color: '#1677ff', marginRight: 4 }} />
                            {taxAmountOverridden ? (
                              <>Ročno spremenil: <b>{intentData?.amount_overridden_by || 'neznano'}</b></>
                            ) : (
                              <>Ročno spreminjate davčni znesek.</>
                            )}
                            {intentData?.tax_system_amount != null && (
                              <><br />Sistemsko izračunan znesek: <MoneyDisplay type={null} val={intentData?.tax_system_amount} /></>
                            )}
                            <Button type="link" size="small" style={{ paddingLeft: 6 }} onClick={handleResetAmountOverride}>
                              Ponastavi
                            </Button>
                          </div>
                        )}
                      </Col>
                    </Row>

                    <Row justify="space-between">
                      <Col span={!taxRateReadOnly && !isMassEdit ? 13 : 24}>
                        <Form.Item
                          name="tax_rate"
                          label={<OverrideLabel label="Davčna stopnja" field="tax_rate" overriddenFields={intentData?.manually_overridden_fields} />}
                          rules={taxRateReadOnly ? [] : [{ required: true, message: 'Prosim vnesite davčno stopnjo' }]}
                        >
                          <TaxRateSelect key={formEpoch} disabled={taxRateReadOnly} />
                        </Form.Item>
                      </Col>

                      {!taxRateReadOnly && !isMassEdit && (
                        <Col span={10}>
                          <Form.Item
                            name="tax_amount"
                            label={<OverrideLabel label="Znesek DDV" field="tax_amount" overriddenFields={intentData?.manually_overridden_fields} />}
                            extra="Vnesi želeni znesek DDV — davčna stopnja se izračuna samodejno."
                          >
                            <DebouncedNumberInput
                              value={form.getFieldValue('tax_amount')}
                              onChange={(val) => {
                                form.setFieldsValue({ tax_amount: val });
                                recalcFrom('tax_amount');
                              }}
                              placeholder="Vnesite znesek DDV"
                              />
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                    </>
                  )}

                  {intentData?.tax_amount && intentData.type != 'TAX' && (
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          name="tax_intent_title"
                          label="Knjižba"
                        >
                          {taxIntentInfoComponent || <span style={{ color: '#aaa', fontSize: 12 }}>Vrednost bo prikazana po kreaciji transakcije</span>}
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  <Divider/>

                  {typeValue == 'TRANSFER' && <TransferFields />}

                  {typeValue == 'TAX' && (
                    <TaxFields
                      onShowRelated={isEdit ? handleShowRelatedIntents : null}
                    />
                  )}

                  {(isMassEdit ? (!typeValue || ['INCOME', 'EXPENSE'].includes(typeValue)) : ['INCOME', 'EXPENSE'].includes(typeValue)) && (
                    <DefaultFields
                      typeValue={typeValue}
                      isIntentEditable={isIntentEditable}
                      repeatPeriod={repeatPeriod}
                      taxIntentInfoComponent={taxIntentInfoComponent}
                      massEdit={isMassEdit}
                      overriddenFields={intentData?.manually_overridden_fields}
                      splitModalOpen={splitModalOpen}
                      setSplitModalOpen={setSplitModalOpen}
                      isEdit={isEdit}
                      editedIntentId={props.editedIntentId}
                      onSplitsUpdated={() => {
                        setRefreshTick(x => x + 1);
                        props.onDone?.();
                      }}
                      splitEditMode={
                        intentData?.split_siblings ? splitEditMode : null
                      }
                      activeEditDimension={activeEditDimension}
                    />
                  )}
                </>
          )}
          {activeDrawerTab === 'comments' && (
            <IntentComments
              intentId={props.editedIntentId}
              isFlagged={intentData?.is_flagged}
              onFlagChange={(val) => {
                setIntentData((prev) => prev ? { ...prev, is_flagged: val } : prev);
                props.onIntentChanged?.();
              }}
              onCountChange={(count) => {
                setIntentData((prev) => prev ? { ...prev, comments_count: count } : prev);
              }}
            />
          )}
          {activeDrawerTab === 'history' && (
            <HistoryTimeline model="Intent" objectId={props.editedIntentId} />
          )}
        </Drawer>
      </Form>

        <BudgetSyncModal
          open={showBudgetSyncModal}
          onOk={(syncMode) => {
            setShowBudgetSyncModal(false);
            handleConfirmedSave(pendingValues, null, syncMode);
          }}
          onCancel={() => {
            setShowBudgetSyncModal(false);
            setPendingValues(null);
          }}
        />

        <WizardUpdateConfirmModal
          open={showWizardUpdateConfirmModal}
          mode={wizardEditMode}
          onOk={() => {
            setshowWizardUpdateConfirmModal(false);
            handleConfirmedSave(pendingValues, wizardEditMode);
          }}
          onCancel={() => {
            setshowWizardUpdateConfirmModal(false);
            setPendingValues(null);
          }}
        />

        <SplitUpdateConfirmModal
          open={showSplitConfirmModal}
          mode={splitEditMode}
          onOk={() => {
            setShowSplitConfirmModal(false);
            handleConfirmedSave(
              pendingValues,
              null,
              null,
              splitEditMode === 'child' ? 'single' : 'all',
            );
          }}
          onCancel={() => {
            setShowSplitConfirmModal(false);
            setPendingValues(null);
          }}
        />

        <ConfirmModal
          textComponent={
            <>
              <p>{`Ali ste prepričani, da želite izbrisati transakcijo #${intentData?.id}?`}</p>
              {intentData?.split_siblings && (
                <p style={{ color: '#d4380d' }}>
                  Pozor: izbrisana bo cela razdelitev (master + vse vrstice te ponovitve).
                </p>
              )}
              {intentData?.wizard && activeEditDimension === 'wizard' && (
                <div style={{ marginTop: 12 }}>
                  <Space>
                    <span
                      style={{
                        fontWeight: wizardEditMode === 'all' ? 600 : 400,
                        opacity: wizardEditMode === 'all' ? 1 : 0.55,
                      }}
                    >
                      Izbris VSEH ponovitev
                    </span>
                    <Switch
                      checked={wizardEditMode === 'single'}
                      onChange={(checked) =>
                        setWizardEditMode(checked ? 'single' : 'all')
                      }
                    />
                    <span
                      style={{
                        fontWeight: wizardEditMode === 'single' ? 600 : 400,
                        opacity: wizardEditMode === 'single' ? 1 : 0.55,
                      }}
                    >
                      Izbris samo TE ponovitve
                    </span>
                  </Space>
                  {wizardEditMode === 'all' && (
                    <div style={{ marginTop: 8 }}>
                      <Checkbox
                        checked={includePastUnpaid}
                        onChange={(e) => setIncludePastUnpaid(e.target.checked)}
                      >
                        Vključi pretekle neplačane ponovitve
                      </Checkbox>
                      <br />
                      <Checkbox
                        checked={includePastPaid}
                        onChange={(e) => setIncludePastPaid(e.target.checked)}
                      >
                        Vključi že plačane ponovitve
                      </Checkbox>
                    </div>
                  )}
                </div>
              )}
              {intentData?.wizard && activeEditDimension !== 'wizard' && (
                <p style={{ fontStyle: 'italic', color: '#8c8c8c' }}>
                  Druge ponovitve ostanejo nespremenjene (urejaš razdelitev,
                  ne ponovitev). Za izbris več ponovitev preklopi na "ponovitev"
                  v zgornjem info-okviru.
                </p>
              )}
            </>
          }
          open={showDeletionConfirmModal}
          confirmBtnTitle='Izbriši'
          confirmBtnProps={{'danger': true}}
          onOk={() => handleConfirmedDelete()}
          onCancel={() => {
            setShowDeletionConfirmModal(false);
          }}
        />

        <ConfirmModal
          textComponent={<p>{`Ali ste prepričani, da želite posodobiti ${props.massEditIds?.length} transakcij?`}</p>}
          open={showMassEditConfirmModal}
          confirmBtnTitle='Posodobi'
          onOk={() => handleMassEditSave(pendingMassEditValues)}
          onCancel={() => {
            setShowMassEditConfirmModal(false);
            setPendingMassEditValues(null);
          }}
        />
    </>
  );
};

export default IntentDrawer;
